<template>
  <b-modal
    v-model="editorShown"
    centered
    size="md-lg"
    no-close-on-backdrop
    header-bg-variant="primary"
    header-text-variant="white"
    header-class="bg-purple"
  >
    <template v-slot:modal-header>
      <div
        class="w-100 d-flex flex-row justify-content-between align-items-center"
      >
        <h5 class="modal-title">{{ formTitle }}</h5>
        <button
          type="button"
          aria-label="Close"
          class="close text-white"
          style="opacity: 1 !important"
          @click="$emit('onHidden')"
        >
          ×
        </button>
      </div>
    </template>
    <template v-slot:modal-footer>
      <div class="text-right mt-1 mr-2">
        <b-button @click="$emit('onHidden')" variant="secondary">
          Cancel
        </b-button>
        <b-button class="ml-2" @click="submitScheduleEvent" variant="primary">
          Save
        </b-button>
      </div>
    </template>
    <b-row>
      <b-col>
        <b-form-group
          label="Patient (Active & Pending only)*"
          :state="formErrors.eoc.length == 0 ? null : false"
          :invalid-feedback="formErrors.eoc.join(', ')"
        >
          <eoc-picker
            :eocs="episodesOfCare"
            v-model="selectedEoc"
            :async="true"
            @search="
              ({ search, loading }) => fetchEocsOptionsPicker(search, loading)
            "
          />
        </b-form-group>
        <b-form-group label="Provider">
          <v-select :options="providerUsers" v-model="form.user" />
        </b-form-group>
        <b-form-group
          label="Date"
          :state="formErrors.date.length == 0 ? null : false"
          :invalid-feedback="formErrors.date.join(', ')"
        >
          <date-picker
            v-model="selectedDate"
            :dateDisabledFn="datesNonWorking"
          />
        </b-form-group>
        <b-form-group
          label="Level of Care*"
          :state="formErrors.loc.length == 0 ? null : false"
          :invalid-feedback="formErrors.loc.join(', ')"
        >
          <v-select
            v-model="form.services"
            :options="levelsOfCareFilterByLocation"
          />
        </b-form-group>
      </b-col>
      <b-col>
        <b-form-group label="Diagnosis codes">
          <multiple-select
            v-model="form.icdCodeIds"
            :options="icdCodes"
            :search="(search, loading) => fetchIcdCodeOptions(search, loading)"
            placeholder="Search for ICD Codes"
            :allowDuplicates="true"
            :onDemand="true"
            :disableCache="true"
            :disableFilter="true"
          />
        </b-form-group>
      </b-col>
    </b-row>
  </b-modal>
</template>

<script>
import moment from "moment";
import { mapActions, mapGetters, mapState } from "vuex";
import scheduleEventServer from "../../server/scheduleEvent";
import eocServer from "../../server/eoc";
import userServer from "../../server/user";
import icdCodeServer from "../../server/icdCode";
import diagnosisCodeServer from "../../server/diagnosisCode";
import locationServer from "../../server/location";

import { validateRequired, validateDate } from "../../helpers/validation";
import EocPicker from "../controls/EocPicker";
import DatePicker from "../controls/DatePicker.vue";
import MultipleSelect from "../controls/MultipleSelect.vue";
import { lightenDarkenColor } from "../../helpers/general";

export default {
  name: "DayInLocAddEditModal",
  components: {
    EocPicker,
    DatePicker,
    MultipleSelect
  },
  props: [
    "scheduleEventId",
    "scheduleEventDefaultData",
    "locationId",
    "providerId"
  ],
  data() {
    return {
      form: {
        participantId: null,
        eoc: null,
        user: null,
        date: null,
        services: [],
        icdCodeIds: []
      },
      formErrors: {
        eoc: [],
        date: [],
        loc: []
      },
      episodesOfCare: [],
      users: [],
      icdCodes: [],
      eocDiagnosisCodes: [],
      prepopulatedIcdCodes: [],
      location: null,
      levelsOfCareFilterByLocation: []
    };
  },
  async mounted() {
    this.fetchEocs();
    await this.fetchUsers();

    let form = { ...this.form };
    this.location = await locationServer.get(this.locationId);
    this.levelsOfCareFilterByLocation = this.levelsOfCare.filter(levelOfCare =>
      this.location.serviceIds.includes(levelOfCare.value)
    );
    let currentUserProvider = this.users.find(
      user => user.value === this.currentUser.id
    )?.data;
    if (this.scheduleEventId) {
      const scheduleEvent = await scheduleEventServer.get(this.scheduleEventId);
      const service = scheduleEvent.services[0].service;
      form = {
        eoc: {
          value: scheduleEvent.episodesOfCare[0].episodeOfCare.id,
          label: this.formatEocObj(
            scheduleEvent.episodesOfCare[0].episodeOfCare
          ),
          data: scheduleEvent.episodesOfCare[0].episodeOfCare
        },
        user:
          scheduleEvent.users && scheduleEvent.users.length
            ? {
                value: scheduleEvent.users[0].id,
                label: this.formatUser(scheduleEvent.users[0]),
                data: scheduleEvent.users[0]
              }
            : null,
        date: this.formatDateOnlyMysql(scheduleEvent.startDateTime),
        services: {
          value: service.id,
          label: `${
            service.billingCodes.length > 0
              ? service.billingCodes[0].cptCode + " - "
              : ""
          } ${service.name}`,
          data: service,
          costPerUnit: service.costPerUnit,
          numberOfUnits: service.numberOfUnits
        },
        icdCodeIds: scheduleEvent.episodesOfCare[0].icdCodes.map(icdCode => ({
          value: icdCode.id,
          label: `${icdCode.code} - ${icdCode.description}`,
          data: icdCode
        }))
      };
    } else if (this.scheduleEventDefaultData) {
      const scheduleEvent = this.scheduleEventDefaultData;
      currentUserProvider = this.users.find(
        user => user.value === this.scheduleEventDefaultData.providerId
      )?.data;
      form.date = this.formatDateOnlyMysql(scheduleEvent.startDateTime);
      form.user = currentUserProvider
        ? {
            value: currentUserProvider.id,
            label: this.formatUser(currentUserProvider),
            data: currentUserProvider
          }
        : null;
    } else {
      form.user = this.users.find(u => u.value == this.providerId);
    }
    if (form.eoc) {
      this.eocDiagnosisCodes = await diagnosisCodeServer.query(
        false,
        form.eoc.value
      );
    }
    this.form = { ...form };
  },
  methods: {
    ...mapActions("layout", ["showAlertSuccess"]),
    ...mapActions("client", [
      "fetchScheduleEventStatuses",
      "fetchPlacesOfService"
    ]),
    validateScheduleEventSubmit() {
      let withoutErrors = true;

      this.formErrors.eoc = [];
      if (!validateRequired(this.form.eoc)) {
        this.formErrors.eoc.push("This field is required");
        withoutErrors = false;
      }
      this.formErrors.loc = [];
      if (!validateRequired(this.form.services)) {
        this.formErrors.loc.push("This field is required");
        withoutErrors = false;
      }
      this.formErrors.date = [];
      if (!validateRequired(this.form.date)) {
        this.formErrors.date.push("This field is required");
        withoutErrors = false;
      } else if (!validateDate(this.form.date)) {
        this.formErrors.date.push("Invalid date");
        withoutErrors = false;
      } else if (
        this.datesNonWorking(null, new Date(this.form.date + " 00:00:00"))
      ) {
        this.formErrors.date.push(
          "Events cannot be created outside hours of operation"
        );
        withoutErrors = false;
      }

      return withoutErrors;
    },
    async submitScheduleEvent(modalEvent) {
      modalEvent.preventDefault();
      const result = await this.validateScheduleEventSubmit();
      if (result) {
        const status = this.statuses.find(s => s.data.code == "show");
        const formData = {
          type: "LOC",
          episodesOfCare: [
            {
              ...(this.form.participantId
                ? { id: this.form.participantId }
                : {}),
              episodeOfCareId: this.form.eoc.value,
              icdCodeIds: this.form.icdCodeIds.map(icdCode => icdCode.value),
              statusId: status.value
            }
          ],
          users: this.form.user ? [this.form.user.value] : [],
          allDay: true,
          startDateTime: this.startDateTime,
          services: [
            {
              serviceId: this.form.services.value,
              costPerUnit: this.form.services.data.costPerUnit,
              numberOfUnits: 1,
              billingCodes: this.form.services.data.billingCodes.map(code => ({
                ...code,
                serviceId: this.form.services.value
              }))
            }
          ],
          modification: "ModifySingleEvent",
          locationId: this.location?.id
        };

        if (this.scheduleEventId) {
          try {
            const dialog = await this.$dialog.confirm(
              {
                title: "Do you want to notify participants?"
              },
              {
                view: "multiple-actions",
                options: [
                  { value: true, label: "Yes" },
                  { value: false, label: "No" }
                ]
              }
            );
            formData.resend = dialog.data.value;
            await scheduleEventServer.update(this.scheduleEventId, formData);
            this.showAlertSuccess();
            this.$emit("onModify");
          } catch {
            return;
          }
        } else {
          const existingEvent = await scheduleEventServer.query({
            startDateFrom: formData.startDateTime,
            startDateTo: formData.startDateTime,
            episodeOfCareIds: [this.form.eoc.value],
            types: [4]
          });

          if (existingEvent.length > 0) {
            this.$dialog
              .confirm(
                {
                  title:
                    "There is an event for this patient at the same time. Do you want to remove it?"
                },
                {
                  view: "confirm-delete"
                }
              )
              .then(async () => {
                await scheduleEventServer.create(formData);
                this.showAlertSuccess();
                this.$emit("onModify");
              })
              .catch(function() {});
          } else {
            await scheduleEventServer.create(formData);
            this.showAlertSuccess();
            this.$emit("onModify");
          }
        }
      } else {
        //this.$dialog.alert("You have some errors in the form");
      }
    },
    async fetchEocs() {
      const optionsResult = await eocServer.getAllEocMinimal(
        {
          searchPhrase: "",
          eocStatus: ["Active", "Pending"],
          locations: this.locationId ? [this.locationId] : [],
          page: 1,
          pageSize: 50
        },
        {
          "Hide-Loading": true
        }
      );
      this.episodesOfCare = optionsResult.episodesOfCare.map(eoc => ({
        value: eoc.id,
        label: this.formatEocObj(eoc),
        data: eoc
      }));
    },
    async fetchEocsOptionsPicker(search, loading) {
      if (this.lastTimeoutEocsOptionsPicker) {
        clearTimeout(this.lastTimeoutEocsOptionsPicker);
        this.lastTimeoutEocsOptionsPicker = null;
      }
      if (search && search.trim().length >= 3) {
        this.episodesOfCare = [];
      }
      this.lastTimeoutEocsOptionsPicker = setTimeout(async () => {
        loading(true);
        if (!search || search.length == 0 || search.length >= 3) {
          this.searchingEocs = true;
          const optionsResult = await eocServer.getAllEocMinimal(
            {
              searchPhrase: search,
              eocStatus:
                !search || search.length == 0 ? ["Active", "Pending"] : [],
              locations: this.locationId ? [this.locationId] : [],
              orderBy:"status",
              page: 1,
              pageSize: 50
            },
            {
              "Hide-Loading": true
            }
          );

          this.episodesOfCare = optionsResult.episodesOfCare.map(eoc => ({
            value: eoc.id,
            label: search,
            data: eoc
          }));
          this.searchingEocs = false;
        }
        loading(false);
      }, 1000);
    },
    async fetchUsers() {
      const users = await userServer.getUserServices();
      this.users = users
        .filter(user => user.providerInfo)
        .map(user => ({
          value: user.id,
          label: this.formatUser(user),
          data: user
        }));
    },
    async fetchIcdCodeOptions(search, loading) {
      if (this.IcdCodesTimeout) {
        clearTimeout(this.IcdCodesTimeout);
        this.IcdCodesTimeout = null;
      }
      if (search && search.trim().length >= 3) {
        this.icdCodes = [];
      }

      this.IcdCodesTimeout = setTimeout(async () => {
        loading(true);
        if (search.trim().length >= 3) {
          const icdCodes = await icdCodeServer.query(
            { searchPhrase: search },
            {
              "Hide-Loading": true
            }
          );
          this.icdCodes = icdCodes.map(icdCode => ({
            value: icdCode.id,
            label: `${icdCode.code} - ${icdCode.description}`,
            data: icdCode
          }));
        } else this.icdCodes = [];
        loading(false);
      }, 1000);
    },
    getEocActiveIcdCodes() {
      const date = moment(this.form.date, "YYYY-MM-DD").toDate();
      return this.eocDiagnosisCodes
        .filter(d => {
          const activeFrom = moment(d.activeFrom, "YYYY-MM-DD").toDate();
          const resolvedBy =
            d.resolvedBy && moment(d.resolvedBy, "YYYY-MM-DD").toDate();
          return (
            (d.status.name == "Active" || d.status.name == "Resolved") &&
            activeFrom <= date &&
            (resolvedBy == null || date <= resolvedBy)
          );
        })
        .map(d => ({
          value: d.icdCode.id,
          label: `${d.icdCode.code} - ${d.icdCode.description}`,
          data: d.icdCode
        }));
    }
  },
  computed: {
    ...mapGetters("layout", ["datesNonWorking", "getCurrentAdjustedDateTime"]),
    ...mapState("client", {
      statuses: state =>
        state.scheduleEventStatuses.map(status => ({
          value: status.id,
          label: status.name,
          data: {
            ...status,
            backgroundColor: lightenDarkenColor(status.color, 85)
          }
        }))
    }),
    ...mapState("layout", {
      currentUser: state => state.currentUser,
      isPracticeSuiteActive: state => state.isPracticeSuiteActive
    }),
    ...mapGetters("client", [
      "selectedLocation",
      "levelsOfCareCurrentLocation"
    ]),
    levelsOfCare() {
      return [
        ...this.levelsOfCareCurrentLocation.map(loc => ({
          value: loc.id,
          label: `${
            loc.billingCodes.length > 0
              ? loc.billingCodes[0].cptCode + " - "
              : ""
          } ${loc.name}`,
          data: loc,
          billingCodes: loc.billingCodes
        }))
      ];
    },
    providerUsers() {
      return this.isPracticeSuiteActive
        ? this.users.filter(u => u.data.providerInfo)
        : this.users;
    },
    startDateTime() {
      if (!this.form.date) {
        return null;
      }
      return this.formatDateTimeSql(this.form.date);
    },
    formTitle() {
      if (this.scheduleEventId) {
        return "Edit Day in Level of Care";
      } else {
        return "New Day in Level of Care";
      }
    },
    eventIsInPast() {
      return (
        !this.startDateTime ||
        this.formatDateTimeMysql(this.startDateTime) <=
          this.formatDateTimeMysql(this.getCurrentAdjustedDateTime())
      );
    },
    selectedEoc: {
      get() {
        return this.form.eoc;
      },
      async set(newValue) {
        if (newValue) {
          this.eocDiagnosisCodes = await diagnosisCodeServer.query(
            false,
            newValue.value
          );
          const newIcdCodes = this.getEocActiveIcdCodes();
          this.form.icdCodeIds = newIcdCodes;

          this.prepopulatedIcdCodes = newIcdCodes;
        }
        this.form.eoc = newValue;
      }
    },
    selectedDate: {
      get() {
        return this.form.date;
      },
      async set(newValue) {
        if (newValue && newValue != this.form.date) {
          if (newValue != this.form.date && this.eocDiagnosisCodes.length > 0) {
            this.form.date = newValue;
            const newIcdCodes = this.getEocActiveIcdCodes();
            this.form.icdCodeIds = newIcdCodes;

            this.prepopulatedIcdCodes = newIcdCodes;
          }
        }
        this.form.date = newValue;
      }
    },
    editorShown: {
      get() {
        return !!this.$attrs.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    }
  },
  watch: {
    "form.icdCodeIds": async function(value) {
      if (value && value.length > 0) {
        this.form.icdCodeIds.forEach(item => {
          const diagnosisCodeElement = this.eocDiagnosisCodes.find(
            x => x.icdCode.id == item.value
          );
          if (
            diagnosisCodeElement != null &&
            item.label.substring(0, 5) != "<span"
          ) {
            const newLabel = diagnosisCodeElement.category
              ? `<span class="badge text-white mr-1 p-1 badge-primary">${diagnosisCodeElement.category[0]}</span> ${item.label}`
              : item.label;
            item.label = newLabel;
          }
        });
      }
    }
  }
};
</script>
